import { attachAuthToken } from '@/core/interceptors/auth.interceptor';
import { attachTenantHeaders } from '@/core/interceptors/tenant.interceptor';

import { logger } from './logger';
import { classifyApiFailure } from './api-log-classification';

/**
 * Did the app believe it had a session when it made this call?
 *
 * This is what separates an expected 401 (a visitor, correctly refused) from
 * an alarming one (a signed-in user whose session just died). Auth itself uses
 * HttpOnly cookies, which JavaScript cannot read — but the client mirrors the
 * access token into localStorage (auth.interceptor.ts), and its presence is a
 * faithful answer to "does this app think it is signed in?", which is exactly
 * the question being asked.
 *
 * Wrapped in try/catch: a private window or blocked site data makes
 * localStorage throw on access, and a logging decision must never be the thing
 * that breaks a request. Unknown is treated as authenticated, so an ambiguous
 * case stays visible in the error log rather than being filtered away.
 */
function hasSession(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return Boolean(localStorage.getItem('access_token'));
  } catch {
    return true;
  }
}

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api/v1';

export type QueryParams = Record<string, string | number | boolean | undefined | null>;

type AnyParams = Record<string, any>;

interface RequestConfig {
  method?: string;
  headers?: Record<string, string>;
  body?: unknown;
  params?: AnyParams;
}

function buildUrl(path: string, params?: AnyParams) {
  const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:6100';
  const url = new URL(`${API_BASE_URL}${path}`, base);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
    });
  }
  return url.toString();
}

function buildHeaders(custom: Record<string, string> = {}): Record<string, string> {
  let headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Apply interceptors in order: auth first, then tenant
  headers = attachAuthToken(headers);
  headers = attachTenantHeaders(headers);

  // Custom headers override interceptor values
  return { ...headers, ...custom };
}

async function request<T>(path: string, config: RequestConfig = {}): Promise<T> {
  const { method = 'GET', headers = {}, body, params } = config;

  const url = buildUrl(path, params);
  const mergedHeaders = buildHeaders(headers);

  const response = await fetch(url, {
    method,
    headers: mergedHeaders,
    body: body ? JSON.stringify(body) : undefined,
    // Send the HttpOnly auth cookie.
    //
    // `fetch` defaults to `same-origin`, which happens to include our own
    // /api/v1 calls — but stating it is not redundant: NEXT_PUBLIC_API_URL can
    // point at a different origin (the api.* split), and on that day a silent
    // default would drop the cookie and log every user out with no code change
    // to blame. Declaring the intent makes the behaviour survive that move.
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    // Routine traffic goes to access.log; only real faults reach error.log.
    // See api-log-classification.ts — the decision is deliberately made there,
    // once, rather than case by case at each call site.
    const destination = classifyApiFailure({
      status: response.status,
      path,
      method,
      authenticated: hasSession(),
      code: (error as { error?: { code?: unknown } })?.error?.code as string | undefined,
    });
    const log = destination === 'access' ? logger.access : logger.error;
    log(`API ${method} ${path} → ${response.status}`, {
      component: 'api-client',
      metadata: { status: response.status, path, method, error },
    });
    throw { status: response.status, ...error };
  }

  return response.json();
}

async function uploadRequest<T>(path: string, formData: FormData): Promise<T> {
  const url = buildUrl(path);
  // For file uploads, do not set Content-Type — the browser will set
  // multipart/form-data with the correct boundary automatically.
  let headers: Record<string, string> = {};
  headers = attachAuthToken(headers);
  headers = attachTenantHeaders(headers);

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
    // Same reasoning as the JSON path above — uploads are authenticated too,
    // and a file upload silently 401ing is a particularly confusing failure.
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Upload failed' }));
    const destination = classifyApiFailure({
      status: response.status,
      path,
      // An upload is a POST — a refused one is a user losing a file, never
      // routine traffic, so it must stay in the error log.
      method: 'POST',
      authenticated: hasSession(),
    });
    const log = destination === 'access' ? logger.access : logger.error;
    log(`API UPLOAD ${path} → ${response.status}`, {
      component: 'api-client',
      metadata: { status: response.status, path, error },
    });
    throw { status: response.status, ...error };
  }

  return response.json();
}

/**
 * A file response (CSV, PDF…) with the same auth, tenant headers and failure
 * logging as JSON calls. The file name comes from Content-Disposition.
 */
async function downloadRequest(path: string, params?: AnyParams): Promise<{ blob: Blob; filename: string | null }> {
  const response = await fetch(buildUrl(path, params), {
    method: 'GET',
    headers: buildHeaders({ Accept: '*/*' }),
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Download failed' }));
    const destination = classifyApiFailure({
      status: response.status,
      path,
      method: 'GET',
      authenticated: hasSession(),
      code: (error as { error?: { code?: unknown } })?.error?.code as string | undefined,
    });
    const log = destination === 'access' ? logger.access : logger.error;
    log(`API DOWNLOAD ${path} → ${response.status}`, {
      component: 'api-client',
      metadata: { status: response.status, path, error },
    });
    throw { status: response.status, ...error };
  }
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const filename = disposition.match(/filename="?([^";]+)"?/)?.[1] ?? null;
  return { blob: await response.blob(), filename };
}

export const api = {
  get: <T>(path: string, config?: { params?: AnyParams }) =>
    request<T>(path, { method: 'GET', ...config }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) =>
    request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) =>
    uploadRequest<T>(path, formData),
  download: (path: string, config?: { params?: AnyParams }) =>
    downloadRequest(path, config?.params),
};
